import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api, releaseAuthBootstrapGate } from '../lib/api';

export interface User {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string;
  deleteRequestedAt: string | null;
  businessName: string;
  employmentRole: string | null;
  isOwner: boolean;
  isSuperAdmin: boolean;
}

/** moduleKey => READ | WRITE | ADMIN (spec Bilaga K) */
export type Permissions = Record<string, string>;

interface AuthBootstrapResponse {
  authenticated: boolean;
  user?: User;
  businessId?: number;
  permissions?: Permissions;
}

interface AuthState {
  user: User | null;
  businessId: number | null;
  permissions: Permissions;
  loading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  canWrite: (moduleCode: string) => boolean;
  canRead: (moduleCode: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    businessId: null,
    permissions: {},
    loading: true,
  });

  const refreshAuth = useCallback(async () => {
    try {
      const response = await api.raw('/auth/bootstrap', { method: 'GET' }, true);
      const data = (await response.json().catch(() => null)) as AuthBootstrapResponse | null;
      if (response.ok && data?.authenticated && data.user && typeof data.businessId === 'number' && data.permissions) {
        setState({
          user: data.user,
          businessId: data.businessId,
          permissions: data.permissions,
          loading: false,
        });
        return;
      }
      setState({ user: null, businessId: null, permissions: {}, loading: false });
    } catch {
      setState({ user: null, businessId: null, permissions: {}, loading: false });
    } finally {
      releaseAuthBootstrapGate();
    }
  }, []);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  const login = useCallback(async (email: string, password: string) => {
    const data = await api.post<{ user: User; businessId: number; permissions: Permissions }>('/auth/login', {
      email,
      password,
    });
    setState({
      user: data.user,
      businessId: data.businessId,
      permissions: data.permissions,
      loading: false,
    });
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setState({ user: null, businessId: null, permissions: {}, loading: false });
    }
  }, []);

  const canWrite = useCallback(
    (moduleCode: string) =>
      state.permissions[moduleCode] === 'WRITE' || state.permissions[moduleCode] === 'ADMIN',
    [state.permissions]
  );

  const canRead = useCallback(
    (moduleCode: string) =>
      ['READ', 'WRITE', 'ADMIN'].includes(state.permissions[moduleCode] ?? ''),
    [state.permissions]
  );

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        refreshAuth,
        canWrite,
        canRead,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

